import * as EXIF from 'exif-js';
import * as exifr from 'exifr';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Form, FormTemplate } from '@nrwl/rx-models';
import { TemplateMode, WidgetActionData, WidgetBase } from '../../../models';
import { catchError, map, take } from 'rxjs/operators';

import { EMPTY, forkJoin, fromEvent, of } from 'rxjs';
import { Ng2ImgMaxService } from 'ng2-img-max';
import { UserFile } from '@nrwl/rx-models';
import { UserFileService } from '@nrwl/rx-service-userfile';
import { WebviewService } from '@nrwl/rx-webview-service';
import { SentryHandlerService } from '@nrwl/rx-service-sentry';
import { FileConvertService } from '@nrwl/rx-files-helpers-service';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { FbAttachmentWidget } from '../../../models/form-widget-attachment.model';
import { RxIconModule } from '@nrwl/rx-icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { RxButtonModule } from '@nrwl/rx-button';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'fb-attachment',
  templateUrl: './fb-attachment.component.html',
  styleUrls: ['./fb-attachment.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TokenToDivPipe,
    RxIconModule,
    RxButtonModule,
    MatDividerModule,
    MatMenuModule,
  ],
})
export class FbAttachmentComponent extends WidgetBase implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;
  @ViewChild('addAttachmentMenu', { static: false }) menu: MatMenu;
  public DEBUG_INFO: any = [];
  public images: any = [];
  public imageLoadQueue: any = [];
  public hostWidth: any;
  // Support for multiple attachment widgets
  public UID: any = 'uid' + Math.random().toString(36).substring(2);
  public busy: any = false;
  public templateMode: any = TemplateMode;

  get hasNativeAttachmentBridge() {
    return this.webviewService.handler('rxEventAttachment')?.standalone !== true;
  }

  get isKiosk() {
    return this.mode === 'kiosk';
  }

  @Input() public override set payload(value) {
    this.ready.next(true);
    this._payload = value;
    let ids = this._payload.properties.value.value || [];

    // Backward compatibility
    ids = ids.map((item: any) => {
      return item.id?.id || item.id || item;
    });

    ids.forEach((id) => {
      this.images.push({
        id: id,
        file: null,
        thumb: null,
        thumb_high_res: null,
        error: false,
        base64: null,
      });
    });
    this.reload();
  }
  public override get payload() {
    return this._payload;
  }

  // iOS event: Attachement recieved
  @HostListener('window:rxEventAttachment', ['$event'])
  rxEventAttachment(event) {
    if (event.detail.attachmentId === this.UID) {
      this.busy = true;
      let base64 = decodeURIComponent(event.detail.data);
      base64 = base64.replace('\r\n', '');
      this.saveAttachment(base64);
      this._cd.detectChanges();
    }
  }

  // [rxEventEditImageDone] - Receiving modified image
  @HostListener('window:rxEventEditImageDone', ['$event'])
  rxEventEditImageDone(event) {
    if (event.detail && event.detail.id === this.UID && event.detail.data) {
      this.busy = true;
      let base64 = decodeURIComponent(event.detail.data);
      base64 = base64.replace('\r\n', '');
      return this.updateImage(base64);
    }
  }

  constructor(
    private injector: Injector,
    private userFileService: UserFileService,
    private _cd: ChangeDetectorRef,
    public webviewService: WebviewService,
    private hostElement: ElementRef<HTMLElement>,
    private ng2ImgMaxService: Ng2ImgMaxService,
    private _sentryHandlerService: SentryHandlerService,
    private render: Renderer2,
    private fileConvertService: FileConvertService,
  ) {
    super(injector);
    this.ready.next(true);
  }

  debugLog(name, value) {
    console.log(name, value);
  }

  ngOnInit() {
    if (
      this.entity &&
      this.entity.status &&
      this.entity.status === 5 &&
      !this.payload.properties.value?.value
    ) {
      this.hostElement.nativeElement.remove();
    }
  }

  ngAfterViewInit(): void {
    this.updateHostWidth();
  }

  public onRemoveImageClick($event: any, image: any): void {
    if (this.isKiosk) {
      this.handleKioskModeAttachment(
        $event,
        'Unable to remove attachment from a form in kiosk mode.',
      );
      return;
    }
    const pos = this.payload.properties.value.value.indexOf(image.id);
    this.payload.properties.value.value.splice(pos, 1);
    this.imageLoadQueue = [];
    this.images = [];
  }

  //#region Initial load userfiles and to base64 conversion

  public reload() {
    this.busy = true;

    if (this.images.length === 0) {
      this.busy = false;
      this.ready.next(true);
      return;
    }

    const userFiles$ = this.images.map((image: any) => {
      return this.userFileService.get(image.id).pipe(
        take(1),
        catchError((error) => {
          this.imageLoadQueue = [];
          this.images = [];
          this.busy = false;
          this.ready.next(true);
          return of({ id: image.id, error: error });
        }),
      );
    });

    forkJoin(userFiles$).subscribe((userFiles: any) => {
      this.preloadUserFiles(userFiles);
    });
  }

  base64ToArrayBuffer(base64) {
    base64 = base64.replace(/^data\:([^\;]+)\;base64,/gim, '');
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  public preloadUserFiles(userFiles: any[]) {
    const base64List = userFiles
      .filter((image) => !image.error)
      .map((image) => {
        return this.fileConvertService
          .toBase64viaCanvasObservable(image.thumb_high_res || image.file)
          .pipe(
            take(1),
            map((item) => {
              image.base64 = item;
              return image;
            }),
            catchError((error) => {
              this.debugLog('ERROR: toBase64viaCanvas:', error);
              image.error = true;
              return of(image);
            }),
          );
      });

    forkJoin(base64List).subscribe((list) => {
      const result = list.filter((item) => !item.error);
      if (result.length) {
        this.imageLoadQueue = [...result];
        this.images = result;
      } else {
        this.imageLoadQueue = [];
        this.images = [];
        this.busy = false;
        this.ready.next(true);
      }
      this._cd.detectChanges();
    });
  }

  //#endregion

  public updateImage(base64: string) {
    this.fileConvertService.resizeBase64Image(
      base64,
      (resizedBase64) => {
        this.userFileService
          .postBase64(resizedBase64)
          .pipe(
            take(1),
            catchError((error) => {
              this.debugLog('ERROR: HostListener window:rxEventEditImageDone:', error);
              return of(null);
            }),
          )
          .subscribe((userFile: UserFile | any) => {
            if (userFile) {
              this.action.emit(
                new WidgetActionData(this, this.payload.properties.value.value, 'migration'),
              );

              this.payload.properties.value.value = [userFile.id];

              const result = [
                {
                  id: userFile.id,
                  file: resizedBase64, // result.file,
                  thumb: resizedBase64, // result.thumb,
                  thumb_high_res: resizedBase64, //result.thumb_high_res
                  base64: resizedBase64,
                  error: false,
                },
              ];

              this.imageLoadQueue = [...result];
              this.images = result;

              this._cd.detectChanges();
            }
          });
      },
      (error) => {},
    );
    //this._base64 = base64;
  }

  //#endregion

  //#region iOS Client Photos handler

  public onClientPhotosClicked($event) {
    if (!this.hasNativeAttachmentBridge) {
      this.onBrowseClicked($event);
      return;
    }

    try {
      this.webviewService.attachment({
        attachmentId: this.UID,
      });
    } catch (e) {}
  }

  // Event from iOS. Send received base64 to the server and display it
  public saveAttachment(base64) {
    this.userFileService
      .postBase64(base64)
      .pipe(
        take(1),
        catchError((error) => {
          this.debugLog('ERROR: userFileService postBase64 from window:rxEventAttachment:', error);
          return of(null);
        }),
      )
      .subscribe((userFile: UserFile | any) => {
        if (userFile) {
          const result = [
            {
              id: userFile.id,
              file: base64, // result.file,
              thumb: base64, // result.thumb,
              thumb_high_res: base64, //result.thumb_high_res
              base64: base64,
              error: false,
            },
          ];
          this.imageLoadQueue = [...result];
          this.images = result;
          this.payload.properties.value.value = this.images.map((item) => item.id);
        }
        this._cd.detectChanges();
      });
  }
  //#endregion

  //#region Browse button & menu handler

  public onFileSelected(event) {
    this.setupReader(event.target.files[0]);
  }

  // WARNING: Input File element is not being removed on cancel
  public onBrowseClicked($event) {
    const input = this.render.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event) => {
      this.setupReader(event.target.files[0]);
      input.remove();
    };
    input.style.display = 'none';
    document.body.appendChild(input);
    input.click();
  }

  private rotateImage(base64: string, orientation: number, mimeType: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        let width = image.width;
        let height = image.height;
        canvas.width = width;
        canvas.height = height;

        // Transform context before drawing image
        switch (orientation) {
          case 2:
            ctx.transform(-1, 0, 0, 1, width, 0);
            break;
          case 3:
            ctx.transform(-1, 0, 0, -1, width, height);
            break;
          case 4:
            ctx.transform(1, 0, 0, -1, 0, height);
            break;
          case 5:
            canvas.width = height;
            canvas.height = width;
            ctx.transform(0, 1, 1, 0, 0, 0);
            break;
          case 6:
            canvas.width = height;
            canvas.height = width;
            ctx.transform(0, 1, -1, 0, height, 0);
            break;
          case 7:
            canvas.width = height;
            canvas.height = width;
            ctx.transform(0, -1, -1, 0, height, width);
            break;
          case 8:
            canvas.width = height;
            canvas.height = width;
            ctx.transform(0, -1, 1, 0, 0, width);
            break;
          default:
            ctx.transform(1, 0, 0, 1, 0, 0);
            break;
        }

        ctx.drawImage(image, 0, 0);

        const rotatedBase64 = canvas.toDataURL(mimeType);
        resolve(rotatedBase64);
      };
      image.onerror = (e: any) => {
        reject(e);
      };

      image.src = base64;
    });
  }

  private uploadBase64(base64: string) {
    this.fileConvertService.resizeBase64Image(
      base64,
      (resizedBase64) => {
        this.userFileService
          .postBase64(resizedBase64)
          .pipe(
            take(1),
            catchError((error) => {
              this.debugLog('ERROR: userFileService postBase64 from onBrowseClicked:', error);
              return of(null);
            }),
          )
          .subscribe((result: UserFile | any) => {
            if (result) {
              const image = {
                id: result.id,
                file: resizedBase64,
                thumb: resizedBase64,
                thumb_high_res: resizedBase64,
                base64: resizedBase64,
                error: false,
              };
              this.imageLoadQueue = [{ ...image }];
              this.images = [image];
              this.payload.properties.value.value = this.images.map((item) => item.id);
            }

            this.busy = false;
            this._cd.detectChanges();
          });
      },
      (error) => {},
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  private stripEXIF(base64: string, mimeType: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        ctx.drawImage(image, 0, 0);
        const strippedBase64 = canvas.toDataURL(mimeType);
        resolve(strippedBase64);
      };
      image.onerror = (e: any) => {
        reject(e);
      };
      image.src = base64;
    });
  }

  private setupReader(file: File) {
    const reader = new FileReader();
    this.busy = true;
    reader.onload = async (e: any) => {
      const arrayBuffer = e.target.result;
      const mimeType = file.type;
      const base64 = `data:${mimeType};base64,${this.arrayBufferToBase64(arrayBuffer)}`;

      try {
        // const exifOrientation = await exifr.orientation(arrayBuffer);
        // const rotatedBase64 = await this.rotateImage(
        //     base64,
        //     exifOrientation,
        //     mimeType,
        // );
        const strippedBase64 = await this.stripEXIF(base64, mimeType);
        this.uploadBase64(strippedBase64);
      } catch (error) {
        this.uploadBase64(base64);
      }
    };
    reader.onerror = (e: any) => {
      this.busy = false;
    };
    reader.onabort = (e: any) => {
      this.busy = false;
    };
    reader.readAsArrayBuffer(file);
  }

  //#endregion

  //#region Image load events

  public onImageLoad($event: any, image: any) {
    this.removeLoadedImageFromQueue(image);
  }

  public onImageLoadError($event: any, image: any): void {
    this.debugLog('ERROR: onImageError', image);
    this.removeLoadedImageFromQueue(image);
  }

  public removeLoadedImageFromQueue(image): void {
    const index = this.imageLoadQueue.findIndex((queueImg) => image.id === queueImg.id);
    if (index > -1) {
      this.imageLoadQueue.splice(index, 1);
    }

    if (!this.imageLoadQueue.length) {
      this.ready.next(true);
      this.busy = false;
    }
    this._cd.detectChanges();
  }

  //#endregion

  public updateHostWidth(): void {
    this.hostWidth = this.hostElement.nativeElement.clientWidth;
  }
  public onWindowResize($event): void {
    this.updateHostWidth();
  }

  public handleKioskModeAttachment(
    $event: any,
    message = 'Unable to attach items to a form in kiosk mode.',
  ): void {
    if (this.isKiosk) {
      this.action.emit(new WidgetActionData(this, { error: { message: message } }, 'migration'));
      $event.stopPropagation();
      $event.preventDefault();
    }
  }

  public canAttach(): boolean {
    return !this.isKiosk;
  }

  //#region iOS rxEventEditImage && rxEventEditImageDone

  public onAttachedImageClicked($event, image): void {
    if (this.isKiosk) {
      this.handleKioskModeAttachment($event);
      return;
    }

    const payload = {
      id: this.UID,
      base64: image.base64,
      branded: false,
    };
    try {
      (window as any).webkit.messageHandlers.rxEventEditImage.postMessage(payload);
    } catch (e) {
      this.debugLog('ERROR: postMessage rxEventEditImage:', e);
    }
  }

  public canRemoveImage(): boolean {
    return !this.isPrinting && !this.isPreview && this.mode !== 'kiosk';
  }

  public getTemplateMode(): TemplateMode {
    if (this.isPrinting) {
      return TemplateMode.Printing;
    }

    if (this.isRxPortal) {
      return TemplateMode.RxPortal;
    }

    if (this.hasNativeAttachmentBridge) {
      return this.isKiosk ? TemplateMode.MobileKiosk : TemplateMode.MobileProvider;
    }

    return TemplateMode.Web;
  }
  override isValid() {
    return new FbAttachmentWidget(this.payload).isValid();
  }
}
